/**
 * @file 系统状态小卡片：调 /healthcheck 与 /ready，展示真实探活结果
 *
 * 为什么分开两个探针：
 *   /healthcheck 只说明进程活着；/ready 才会连 DB/Redis/对象存储。
 *   两者都要展示，避免「进程活着但依赖挂了」被显示成健康（PRD 12 章「异常不掩盖」）。
 *   ready 返回 503 时信封是 ErrorEnvelope，依赖明细按约定放在 details[].field/reason。
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, type Health, type Ready } from '../api';
import { useRuntime } from '../auth/context';

/** 一次探活快照。 */
interface Snapshot {
  /** 是否在请求中。 */
  loading: boolean;
  /** /healthcheck 结果。 */
  health: Health | null;
  /** /ready 结果（成功时）。 */
  ready: Ready | null;
  /** /ready 失败（典型是 503 SYS_NOT_READY）。 */
  readyError: ApiError | null;
  /** 最近一次检查时间。 */
  checkedAt: Date | null;
}

/** 依赖状态 → 徽标类名。 */
const BADGE_CLASS: Record<string, string> = {
  up: 'lx-badge lx-badge--up',
  ready: 'lx-badge lx-badge--up',
  degraded: 'lx-badge lx-badge--degraded',
  down: 'lx-badge lx-badge--down',
  not_ready: 'lx-badge lx-badge--down',
  skip: 'lx-badge lx-badge--skip',
};

/** 依赖状态 → 中文标签；运维口径，不用英文原值。 */
const STATUS_TEXT: Record<string, string> = {
  up: '正常',
  ready: '就绪',
  degraded: '降级',
  down: '故障',
  not_ready: '未就绪',
  skip: '未启用',
};

/**
 * @what 状态徽标
 * @param props.status up/degraded/down/skip/unknown
 * @return JSX
 */
function StatusBadge({ status }: { status: string }) {
  const cls = BADGE_CLASS[status] ?? 'lx-badge';
  return (
    <span className={cls}>
      <span className="lx-dot" />
      {STATUS_TEXT[status] ?? status}
    </span>
  );
}

/**
 * @what 系统状态卡片
 * @return JSX
 */
export function SystemStatusCard() {
  const { api } = useRuntime();
  const [snapshot, setSnapshot] = useState<Snapshot>({
    loading: true,
    health: null,
    ready: null,
    readyError: null,
    checkedAt: null,
  });

  /**
   * @what 并发拉两个探针；ready 的 503 不当异常抛出，落进 readyError 展示明细
   * @return Promise<void> 无返回值
   * @side 调 /healthcheck 与 /ready（均免鉴权）
   */
  const load = useCallback(async (): Promise<void> => {
    setSnapshot((prev) => ({ ...prev, loading: true }));
    const [healthResult, readyResult] = await Promise.allSettled([api.healthcheck(), api.ready()]);

    const health = healthResult.status === 'fulfilled' ? healthResult.value : null;
    const ready = readyResult.status === 'fulfilled' ? readyResult.value : null;
    const readyError =
      readyResult.status === 'rejected' && readyResult.reason instanceof ApiError ? readyResult.reason : null;

    setSnapshot({ loading: false, health, ready, readyError, checkedAt: new Date() });
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const { health, ready, readyError, loading, checkedAt } = snapshot;

  const healthDown = health?.status === 'down';
  const dependencyDown = Boolean(readyError) || ready?.status === 'not_ready';
  const overall = healthDown ? 'down' : dependencyDown ? 'degraded' : health ? 'up' : 'unknown';

  const dependencyRows =
    readyError?.details.map((item) => ({ name: item.field ?? 'unknown', reason: item.reason ?? '-' })) ?? [];
  const checkRows = ready?.checks ?? [];

  return (
    <section className="lx-card" aria-label="系统状态">
      <div className="lx-card__head">
        <h2 className="lx-card__title">系统状态</h2>
        <StatusBadge status={overall} />
        <span className="lx-card__meta">
          {checkedAt ? `最近检查 ${checkedAt.toLocaleTimeString('zh-CN', { hour12: false })}` : '尚未检查'}
        </span>
        <button type="button" className="lx-btn lx-btn--text" onClick={() => void load()} disabled={loading}>
          {loading ? '检查中…' : '刷新'}
        </button>
      </div>

      <div className="lx-card__body">
        <div className="lx-status__row">
          <span className="lx-status__name">进程健康</span>
          {health ? <StatusBadge status={health.status ?? 'unknown'} /> : <span className="lx-status__value">不可达</span>}
        </div>
        <div className="lx-status__row">
          <span className="lx-status__name">依赖就绪</span>
          {readyError ? (
            <StatusBadge status="down" />
          ) : ready ? (
            <StatusBadge status={ready.status === 'ready' ? 'up' : 'degraded'} />
          ) : (
            <span className="lx-status__value">不可达</span>
          )}
        </div>
        <div className="lx-status__row">
          <span className="lx-status__name">版本</span>
          <span className="lx-status__value">{health?.version ?? '—'}</span>
        </div>
        <div className="lx-status__row">
          <span className="lx-status__name">运行时长</span>
          <span className="lx-status__value">
            {typeof health?.uptime_seconds === 'number' ? `${Math.floor(health.uptime_seconds / 60)} 分钟` : '—'}
          </span>
        </div>

        {readyError ? (
          <div style={{ marginTop: 12 }}>
            <div className="lx-card__hint" style={{ marginBottom: 6 }}>
              {readyError.code === 'SYS_NOT_READY' ? '依赖未就绪，明细如下（503）' : `探活失败：${readyError.message}`}
            </div>
            {dependencyRows.length === 0 ? (
              <div className="lx-card__hint">接口未返回依赖明细（details 为空）</div>
            ) : (
              dependencyRows.map((row) => (
                <div className="lx-dep" key={row.name}>
                  <div className="lx-dep__head">
                    <span className="lx-mono">{row.name}</span>
                    <StatusBadge status="down" />
                  </div>
                  <div className="lx-dep__detail">{row.reason}</div>
                </div>
              ))
            )}
          </div>
        ) : null}

        {!readyError && checkRows.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            {checkRows.map((row, index) => (
              <div className="lx-dep" key={row.name ?? `dep-${index}`}>
                <div className="lx-dep__head">
                  <span className="lx-mono">{row.name ?? '—'}</span>
                  <StatusBadge status={row.status ?? 'unknown'} />
                </div>
                <div className="lx-dep__detail">
                  {typeof row.latency_ms === 'number' ? `${row.latency_ms}ms · ` : ''}
                  {row.detail ?? '—'}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="lx-card__hint" style={{ marginTop: 10 }}>
          {readyError?.traceId ? `追踪号 ${readyError.traceId}` : '进入页面时检查一次；探活只读，不写业务表'}
        </div>
      </div>
    </section>
  );
}
